import React, { useCallback, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import AutomationIcon from '@atlaskit/icon/core/automation';
import { usePublish } from '@atlaskit/rovo-triggers/main';

import {
	type ConversationAction,
	HandleActionType,
} from '@atlassian/rovo-conversation-actions/types';

import { keys } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';
import { useSpaceKey } from '@confluence/space-utils/entry-points/useSpaceKey.ts';

import type { AutomationNudgeUser } from '../hooks/useConfluenceAutomationNudgeActions.tsx';
import { meetingNotesNudgeI18n } from '../i18n.ts';
import { EventSourceName } from '../utils/analytics.ts';
import { markDiscoveryExperienceAsSeen } from '../utils/localStorage.ts';
import { resolvePromptTemplate } from '../utils/prompt-template.ts';

import { MEETING_NOTES_PROMPT_CONFIG } from './meetingNotesPrompt.ts';

export const MEETING_NOTES_NUDGE_ID = 'automation-discovery-meeting-notes-nudge';
const ROVO_CHAT_SOURCE = MEETING_NOTES_NUDGE_ID;

interface UseMeetingNotesNudgeActionProps {
	isEnabled: boolean;
	/** The settled, reactive current-user slice from the parent query. */
	user?: AutomationNudgeUser | null;
}

/**
 * Builds the Meeting Notes discovery nudge `ConversationAction` — its title, icon, the Rovo chat it
 * publishes on click, and the localStorage dismissal bookkeeping — given whether the nudge is
 * currently `enabled`.
 */
export const useMeetingNotesNudgeAction = ({
	isEnabled,
	user,
}: UseMeetingNotesNudgeActionProps): ConversationAction | null => {
	const { formatMessage } = useIntl();
	const publishRovoChat = usePublish('ai-mate');
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const spaceKey = useSpaceKey() ?? undefined;

	useEffect(() => {
		if (isEnabled) {
			createAnalyticsEvent({
				type: 'sendScreenEvent',
				data: {
					name: EventSourceName.MEETING_NOTES,
					source: EventSourceName.MEETING_NOTES,
				},
			}).fire();
		}
	}, [createAnalyticsEvent, isEnabled]);

	const emails = user?.emails;
	const userEmail = useMemo(() => {
		const candidates = emails?.filter((email) => Boolean(email?.value)) ?? [];
		return candidates.find((email) => email?.isPrimary)?.value ?? candidates[0]?.value ?? null;
	}, [emails]);

	const handleLocalStorageUpdate = useCallback(() => {
		markDiscoveryExperienceAsSeen(keys.AUTOMATION_DISCOVER_MEETING_NOTES_TEMPLATE);
	}, []);

	return useMemo<ConversationAction | null>(
		() =>
			isEnabled
				? {
						id: MEETING_NOTES_NUDGE_ID,
						title: formatMessage(meetingNotesNudgeI18n.title),
						secondaryTitle: formatMessage(meetingNotesNudgeI18n.secondaryTitle),
						icon: () => (
							<AutomationIcon size="small" label={formatMessage(meetingNotesNudgeI18n.iconLabel)} />
						),
						handleType: HandleActionType.Skill,
						type: 'skill-action',
						onClick: () => {
							createAnalyticsEvent({
								type: 'sendUIEvent',
								data: {
									action: 'clicked',
									actionSubject: 'button',
									actionSubjectId: 'automationDiscoveryWhenPublishingMeetingNotesProceed',
									source: EventSourceName.MEETING_NOTES,
									attributes: {
										spaceKey,
										template: 'scheduleRecurringPublishMeetingNotesPage',
									},
								},
							}).fire();
							publishRovoChat({
								type: 'chat-new',
								source: ROVO_CHAT_SOURCE,
								interactionSource: ROVO_CHAT_SOURCE,
								openChat: true,
								openChatMode: 'sidebar',
								data: {
									dialogues: [],
									prompt: resolvePromptTemplate(MEETING_NOTES_PROMPT_CONFIG, {
										USER_EMAIL: `${userEmail ?? null}`,
									}),
									placeholderType: 'skill',
									resultAttributionContext: {
										launchSource: ROVO_CHAT_SOURCE,
									},
								},
							});
						},
						nudgeContext: {
							id: MEETING_NOTES_NUDGE_ID,
							selfEligible: true,
							onAccept: handleLocalStorageUpdate,
							onDismiss: (reason) => {
								if (reason?.dismissedMethod === 'manual') {
									handleLocalStorageUpdate();
								}
								createAnalyticsEvent({
									type: 'sendUIEvent',
									data: {
										action: 'clicked',
										actionSubject: 'button',
										actionSubjectId: 'automationDiscoveryWhenPublishingMeetingNotesCancel',
										source: EventSourceName.MEETING_NOTES,
										attributes: {
											spaceKey,
										},
									},
								}).fire();
							},
						},
					}
				: null,
		[
			formatMessage,
			handleLocalStorageUpdate,
			publishRovoChat,
			isEnabled,
			userEmail,
			createAnalyticsEvent,
			spaceKey,
		],
	);
};
